import CircleWarningIcon from '@assets/icons/circle-warning.svg';
import i18n from '@/utils/i18n';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  adaptCustomerDetailRowForCostChanges,
  applyEditToCustomerDetailRow,
  PricingCustomerDetailRow,
  useCustomerDetailTransformer,
} from '@sales-portal/domain/pricing/screens/customers/blocs/transformers/use-customer-detail-transformer';
import { applyPricingFilters } from '@sales-portal/domain/pricing/blocs/utils/pricing-filters';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import {
  bookmarkKey,
  usePricingBookmarksStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-bookmarks-store';
import {
  BookmarkPair,
  useBookmarkActionForPairs,
} from '@sales-portal/domain/pricing/blocs/hooks/use-bookmark-action';
import { buildAccountKey } from '@sales-portal/domain/pricing/blocs/utils/account-key';
import { buildCustomerDetailAggregateRow } from '@/experiences/sales-portal/domain/pricing/screens/customers/blocs/utils/customer-detail-aggregation';
import { buildCustomerDetailFilterSections } from '@sales-portal/domain/pricing/screens/customers/blocs/utils/pricing-customer-detail-filter-sections';
import {
  BulkEditPivot,
  useBulkEditingCore,
} from '@sales-portal/domain/pricing/blocs/hooks/use-bulk-editing-core';
import { Button, Checkbox } from '@usepepper/sprout-kit-ui';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';
import {
  COLUMN_WIDTH,
  PRICING_CONTENT_MIN_WIDTH,
  PRICING_FILTER_PANEL_WIDTH,
  PRICING_TABLE_LAYOUT,
} from '@sales-portal/domain/pricing/constants';
import { ColumnId } from '@sales-portal/domain/pricing/screens/management/types';
import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import { ConfigureColumnsButton } from '@experiences/components/table/configure-columns-button';
import { createStyles } from '@/contexts/theme-context';
import {
  customerDetailGlobalFilterFn,
  isDeltaColumn,
  isLeftAlignedColumn,
} from '@/experiences/sales-portal/domain/pricing/screens/customers/blocs/utils/table-ui';
import { DetailColumnId } from '@sales-portal/domain/pricing/screens/customers/types';
import { EmptyState } from '@experiences/components/empty-state';
import {
  formatCurrency,
  getCurrencySymbol,
  MICRO_CONVERSION,
} from '@/utils/money';
import { formatPercentage } from '@sales-portal/blocs/utils/money';
import { getTranslationValue } from '@/utils/translations';
import { ItemInputCell } from '@sales-portal/domain/pricing/screens/management/blocs/containers/item-input-cell';
import { ItemUpdatedPricingCellContainer } from '@sales-portal/domain/pricing/screens/management/blocs/containers/item-updated-pricing-cell-container';
import { LayoutChangeEvent, View } from 'react-native';
import { percentOfPrevious } from '@sales-portal/domain/pricing/screens/management/blocs/utils/variant-pack-cost-change-pricing';
import { PricingAggregateRow } from '@/experiences/sales-portal/domain/pricing/components/pricing-aggregate-row';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
  PricingPageSource,
} from '@sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { PricingBreadcrumbs } from '@sales-portal/domain/pricing/components/pricing-breadcrumbs';
import { PricingBulkEditBottomBar } from '@sales-portal/domain/pricing/screens/management/components/pricing-bulk-edit-bottom-bar';
import { PricingCustomerDetailTableEmptyComponent } from '@/experiences/sales-portal/domain/pricing/screens/customers/components/pricing-customer-detail-table-empty-component';
import { PricingCustomerDetailTableRow } from '@/experiences/sales-portal/domain/pricing/screens/customers/components/pricing-customer-detail-table-row';
import { PricingEditField } from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { PricingFilterButton } from '@sales-portal/domain/pricing/components/pricing-filter-button';
import { PricingFilterEmptyState } from '@sales-portal/domain/pricing/components/pricing-filter-empty-state';
import { PricingFilterPanel } from '@sales-portal/domain/pricing/components/pricing-filter-panel';
import {
  PricingFilterScope,
  PricingFilterUnit,
  PricingRangeColumnConfig,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { PricingReviewButton } from '@sales-portal/domain/pricing/screens/management/components/pricing-review-button';
import { PricingSearchBar } from '@sales-portal/domain/pricing/components/pricing-search-bar';
import { PricingSelectAllRow } from '@sales-portal/domain/pricing/screens/management/components/pricing-select-all-row';
import { PricingTableDeltaCell } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-delta-cell';
import { PricingTableHeader } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-header';
import { PricingTableHeaderCells } from '@sales-portal/domain/pricing/components/pricing-table-header-cells';
import { PricingTableHeaderLabel } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-header-label';
import { PricingTableInputPlaceholder } from '@sales-portal/domain/pricing/screens/customers/components/pricing-table-input-placeholder';
import { PricingTableNameLink } from '@sales-portal/domain/pricing/components/pricing-table-name-link';
import { PricingTableSkeleton } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-skeleton';
import { PricingUndoButton } from '@sales-portal/domain/pricing/screens/management/components/pricing-undo-button';
import {
  RenderHeaderRowCallback,
  RenderRowCallback,
} from '@experiences/components/table/types';
import { resolveDelta } from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import {
  restaurantScope,
  scopeStorageKey,
  selectCustomerEditsCount,
  usePricingEditsStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import {
  SalesPortalKeyboardScope,
  SalesPortalShortcuts,
} from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { SalesPortalStackParams } from '@/navigators/types';
import {
  SearchInput,
  SearchInputRef,
} from '@/experiences/sales-portal/components/search-input';
import { SearchSkeleton } from '@/experiences/sales-portal/components/search-skeleton';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { Table } from '@experiences/components/table/table';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useBulkQuickActions } from '@sales-portal/domain/pricing/blocs/hooks/use-bulk-quick-actions';
import { useBusinessOrganizationPricingConfig } from '@sales-portal/blocs/hooks/use-business-organization-pricing-config';
import { useConfigureColumns } from '@experiences/components/table/blocs/hooks/use-configure-columns';
import { useCustomerDetailDataLoader } from '@sales-portal/domain/pricing/screens/customers/blocs/loaders/customer-detail-data-loader';
import { useFocusedPricingEditsStore } from '@sales-portal/domain/pricing/blocs/hooks/use-focused-pricing-edits-store';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { usePricingEditsTimezoneSync } from '@sales-portal/domain/pricing/screens/management/blocs/hooks/use-pricing-edits-timezone-sync';
import { usePricingFilterPanel } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filter-panel';
import { usePricingFilters } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters';
import { usePricingFiltersUrlSync } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters-url-sync';
import { usePricingSearch } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-search';
import { usePricingSortSnapshot } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-sort-snapshot';
import { useRowSelection } from '@sales-portal/domain/pricing/blocs/hooks/use-row-selection';
import { useScreenScopedKeyboardShortcuts } from '@experiences/platform/shortcuts/use-screen-scoped-keyboard-shortcuts';
import { WeeklyPurchaseQuantitiesCell } from '@sales-portal/domain/pricing/screens/management/components/weekly-purchase-quantities-cell';

const columnHelper = createColumnHelper<PricingCustomerDetailRow>();

const getRowId = (row: PricingCustomerDetailRow): string => row.variantPackUUID;

const isCustomerDetailRowEditable = (row: PricingCustomerDetailRow): boolean =>
  row.priceInMicros !== null &&
  row.costInMicros !== null &&
  row.grossMarginPercentage !== null;

const isRowLocked = (row: PricingCustomerDetailRow): boolean =>
  !isCustomerDetailRowEditable(row);

// Both sides are nullable here, unlike on the cost-change row types.
const percentOfLast = (
  deltaInMicros: number | null,
  lastInMicros: number | null,
): number | null =>
  deltaInMicros === null || lastInMicros === null
    ? null
    : percentOfPrevious(deltaInMicros, lastInMicros);

type DetailCellRenderInput = CellContext<PricingCustomerDetailRow, string> & {
  /** Rendered row index, injected by the row component. */
  index?: number;
  isAggregateRow?: boolean;
};

type Props = StackScreenProps<
  SalesPortalStackParams,
  typeof SALES_PORTAL_ROUTES.PricingCustomerDetail
>;

const detailRowKey = (row: PricingCustomerDetailRow): string =>
  row.variantPackUUID;

export const PricingCustomerDetailScreen: React.FC<Props> = (props) => {
  const { route } = props;
  const { restaurantUUID, supplierUUID } = route.params;
  const {
    costChangeLookbackMode,
    costChangeWeekAnchorDay,
    hasBookmarkingAccess,
    isHistoricalPricingContextEnabled,
  } = useBusinessOrganizationPricingConfig();
  const [styles, theme] = useStyles();
  const [tableLayoutWidth, setTableLayoutWidth] = useState<number | null>(null);
  const handleTableLayout = useCallback(
    (event: LayoutChangeEvent) =>
      setTableLayoutWidth(event.nativeEvent.layout.width),
    [],
  );
  const { clearSearch, debouncedSearchText, searchText, setSearchText } =
    usePricingSearch();
  const [sorting, setSorting] = useState<SortingState>([
    { desc: true, id: DetailColumnId.Volume },
  ]);

  const hasPriceEdits = useFocusedPricingEditsStore(
    (state) => state.edits.length > 0,
  );
  const customerEditsCount = useFocusedPricingEditsStore((state) =>
    selectCustomerEditsCount(state, restaurantUUID),
  );
  const edits = useFocusedPricingEditsStore((state) => state.edits);
  const editScope = useMemo(
    () => restaurantScope(restaurantUUID),
    [restaurantUUID],
  );
  const hasUndoHistory = useFocusedPricingEditsStore(
    (state) =>
      (state.editSnapshotStack[scopeStorageKey(editScope)]?.length ?? 0) > 0,
  );
  const undoLastEdit = usePricingEditsStore((state) => state.undo);
  const resetUndoHistory = usePricingEditsStore(
    (state) => state.resetUndoHistory,
  );

  const handleUndo = useCallback(() => {
    undoLastEdit(editScope);
    pricingManagementAnalytics.track(PricingAnalyticEvent.UNDO, {
      pageSource: PricingPageSource.CUSTOMER_DETAIL,
    });
  }, [editScope, undoLastEdit]);

  const { currencyCode, timezone, timezoneLoading } =
    usePricingEditsTimezoneSync();

  const navigation =
    useNavigation<StackNavigationProp<SalesPortalStackParams>>();

  const { data, loading, error, refetch } = useCustomerDetailDataLoader(
    supplierUUID,
    restaurantUUID,
  );

  const priceEditDisplayHeaders =
    data?.getCustomerDetailData.price_edit_display_headers;

  const hasTrackedLoad = useRef(false);
  useEffect(() => {
    if (!loading && !error && data && !hasTrackedLoad.current) {
      hasTrackedLoad.current = true;
      pricingManagementAnalytics.track(
        PricingAnalyticEvent.LOAD_COST_CHANGES_CUSTOMER_DETAIL,
        {},
      );
    }
  }, [loading, error, data]);

  const { header, rows } = useCustomerDetailTransformer(data);

  const priceFloorEnabled = header?.priceFloorEnabled ?? false;
  const priceFloorMode = header?.priceFloorMode ?? null;

  const mergedRows = useMemo(() => {
    if (edits.length === 0) {
      return rows;
    }
    const editsByKey = new Map(
      edits.map((edit) => [buildAccountKey(edit), edit]),
    );
    return rows.map((row) => {
      const edit = editsByKey.get(buildAccountKey(row));
      return edit ? applyEditToCustomerDetailRow(row, edit) : row;
    });
  }, [edits, rows]);

  const { onSortingChange, resolveEditedRow } = usePricingSortSnapshot({
    keyOf: detailRowKey,
    rows: mergedRows,
    setSorting,
    source: data,
  });

  const {
    clearSelection,
    filteredCount,
    isAllLocked,
    isAllSelected,
    isPartiallySelected,
    onFilteredRowsChange,
    selectedCount,
    selectedUUIDs: selectedVariantPackUUIDs,
    toggleAllVisible,
    toggleSelection: toggleVariantPackSelection,
  } = useRowSelection<PricingCustomerDetailRow>({
    getRowId,
    isLocked: isRowLocked,
  });

  useFocusEffect(
    useCallback(() => {
      resetUndoHistory(editScope);
      clearSelection();
      setSearchText('');
    }, [clearSelection, editScope, resetUndoHistory, setSearchText]),
  );

  const adaptedMergedRows = useMemo(
    () => mergedRows.map((row) => adaptCustomerDetailRowForCostChanges(row)),
    [mergedRows],
  );
  // Keyed so cells can hand the same adapted object to their memos each render.
  const adaptedRowsByVariantPack = useMemo(
    () =>
      new Map(
        rows.map((row) => [
          row.variantPackUUID,
          adaptCustomerDetailRowForCostChanges(row),
        ]),
      ),
    [rows],
  );
  const adaptedRows = useMemo(
    () => [...adaptedRowsByVariantPack.values()],
    [adaptedRowsByVariantPack],
  );

  const {
    applyBulkInput: handleApplyBulkInput,
    bulkEditField,
    bulkEditMode,
    bulkInputValue,
    bulkQuickActionStates,
    canApplyBulkInput,
    setBulkEditField,
    setBulkEditMode,
    setBulkInputValue,
  } = useBulkEditingCore({
    currencyCode,
    mergedRows: adaptedMergedRows,
    pivot: BulkEditPivot.Restaurant,
    pivotUUID: restaurantUUID,
    priceFloorEnabled,
    priceFloorMode,
    pricingEdits: edits,
    rows: adaptedRows,
    scope: editScope,
    selectedKeys: selectedVariantPackUUIDs,
  });

  const selectedPairs = useMemo<BookmarkPair[]>(
    () =>
      [...selectedVariantPackUUIDs].map((packUUID) => ({
        restaurant_id: restaurantUUID,
        variant_pack_id: packUUID,
      })),
    [restaurantUUID, selectedVariantPackUUIDs],
  );
  const bookmarkSupplierUUIDs = useMemo(
    () => (supplierUUID ? [supplierUUID] : []),
    [supplierUUID],
  );

  const {
    bookmarkAction,
    isNoteOpen: isBookmarkNoteOpen,
    onSaveNote: onSaveBookmarkNote,
  } = useBookmarkActionForPairs({
    canBookmarkItem: hasBookmarkingAccess,
    pageSource: PricingPageSource.CUSTOMER_DETAIL,
    pairs: selectedPairs,
    supplierUUIDs: bookmarkSupplierUUIDs,
  });

  const bulkQuickActions = useBulkQuickActions({
    bookmarkAction,
    bulkQuickActionStates,
  });

  const isBulkEditBarVisible = selectedCount > 0 && !timezoneLoading;

  const searchInputRef = useRef<SearchInputRef>(null);

  // Keyboard shortcut: / to focus search input
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.Search,
    () => searchInputRef.current?.focus(),
    [],
    {},
    true,
    SalesPortalKeyboardScope.PRICING_MANAGEMENT,
  );

  const aggregateRow = useMemo(
    () => (header ? buildCustomerDetailAggregateRow(header, mergedRows) : null),
    [header, mergedRows],
  );

  const handleSearchChange = (text: string) => setSearchText(text);

  // Include dynamically fetched columns so the percentage split stays accurate.
  const NUMERIC_COLUMN_PERCENTAGE =
    (100 - PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE) /
    (PRICING_TABLE_LAYOUT.REGULAR_COLUMN_COUNT +
      (priceEditDisplayHeaders?.length ?? 0));

  const columns = useMemo<ColumnDef<PricingCustomerDetailRow, string>[]>(() => {
    const adaptedRow = (row: PricingCustomerDetailRow) =>
      adaptedRowsByVariantPack.get(row.variantPackUUID) ??
      adaptCustomerDetailRowForCostChanges(row);

    return [
      // Checkbox
      columnHelper.display({
        cell: ({ row }) => {
          const isRowSelected = selectedVariantPackUUIDs.has(
            row.original.variantPackUUID,
          );
          const isEditable = isCustomerDetailRowEditable(row.original);
          return (
            <View style={[styles.checkboxContainer, styles.checkbox]}>
              <Checkbox
                checked={isRowSelected}
                disabled={!isEditable}
                onPress={() =>
                  toggleVariantPackSelection(row.original.variantPackUUID)
                }
                size="$sm"
                tabIndex={-1}
                testID={`pricing-select-row-${row.original.variantPackUUID}${isRowSelected ? '-checked' : ''}`}
              />
            </View>
          );
        },
        header: () => null,
        id: DetailColumnId.Checkbox,
        meta: {
          hideable: false,
        },
        size: PRICING_TABLE_LAYOUT.CHECKBOX_COLUMN_WIDTH,
      }),
      // Item Name (pinned)
      columnHelper.accessor((row) => row.variantPackName, {
        cell: (info: DetailCellRenderInput) => {
          if (info.isAggregateRow) {
            return (
              <Typography variant="paragraph-md-600">
                {info.getValue()}
              </Typography>
            );
          }
          return (
            <PricingTableNameLink
              name={info.getValue()}
              onPress={() =>
                navigation.navigate(SALES_PORTAL_ROUTES.PricingItemManagement, {
                  supplierUUID: info.row.original.supplierUUID,
                  variantPackUUID: info.row.original.variantPackUUID,
                })
              }
            />
          );
        },
        header: () => i18n.t('pricing.table.header.name'),
        id: DetailColumnId.Name,
        meta: {
          alwaysVisible: true,
          initialPercentWidth: PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE,
          label: i18n.t('pricing.table.header.name'),
        },
        minSize: PRICING_TABLE_LAYOUT.NAME_COLUMN_MIN_WIDTH,
        sortingFn: 'alphanumeric',
      }),
      // Item Code
      columnHelper.accessor((row) => row.variantPackId, {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.primary}
            variant="paragraph-md"
          >
            {info.getValue()}
          </Typography>
        ),
        header: () => i18n.t('pricing.table.header.id'),
        id: DetailColumnId.ItemCode,
        meta: {
          initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
          label: i18n.t('pricing.table.header.id'),
        },
        ...COLUMN_WIDTH.M,
        sortingFn: 'alphanumeric',
      }),
      // UOM
      columnHelper.accessor((row) => row.variantPackUOM, {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.primary}
            textTransform="capitalize"
            variant="paragraph-md"
          >
            {info.getValue()}
          </Typography>
        ),
        header: () => i18n.t('pricing.table.header.uom'),
        id: DetailColumnId.UOM,
        meta: {
          initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
          label: i18n.t('pricing.table.header.uom'),
        },
        ...COLUMN_WIDTH.M,
        sortingFn: 'alphanumeric',
      }),
      // Realized GM%
      columnHelper.accessor(
        (row) =>
          row.lastMarginPercentage === null
            ? '--'
            : formatPercentage(row.lastMarginPercentage, 1, 1),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => (
            <PricingTableHeaderLabel
              label={i18n.t('pricing.table.header.realized_gross_margin')}
              tooltipText={i18n.t(
                'pricing.table.header.realized_gross_margin.tooltip_text',
              )}
            />
          ),
          id: DetailColumnId.RealizedGrossMargin,
          meta: {
            defaultHidden: true,
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.realized_gross_margin'),
          },
          ...COLUMN_WIDTH.S,
          sortUndefined: 'last',
        },
      ),
      // Current GM% — editable
      columnHelper.accessor(
        (row) =>
          row.grossMarginPercentage === null
            ? '--'
            : formatPercentage(row.grossMarginPercentage, 1, 1),
        {
          cell: (info: DetailCellRenderInput) => {
            const row = info.row.original;
            if (
              info.isAggregateRow ||
              row.grossMarginPercentage === null ||
              row.priceInMicros === null ||
              row.costInMicros === null
            ) {
              return <PricingTableInputPlaceholder value={info.getValue()} />;
            }
            return (
              <ItemInputCell
                canBookmarkItem={hasBookmarkingAccess}
                columnId={info.column.id}
                currencyCode={currencyCode}
                disabled={false}
                inputType={PricingEditField.GrossMarginPercentage}
                isHistoricalPricingContextEnabled={
                  isHistoricalPricingContextEnabled
                }
                originalFormattedValue={formatPercentage(
                  row.grossMarginPercentage,
                  1,
                )}
                originalRow={adaptedRow(row)}
                pageSource={PricingPageSource.CUSTOMER_DETAIL}
                priceFloorEnabled={priceFloorEnabled}
                priceFloorMode={priceFloorMode}
                restaurantUUID={row.restaurantUUID}
                rowIndex={info.row.id}
                scope={editScope}
                tableRowIndex={info.index ?? info.row.index}
                value={String(parseFloat(row.grossMarginPercentage.toFixed(1)))}
                variantPackUUID={row.variantPackUUID}
              />
            );
          },
          header: () => (
            <PricingTableHeaderLabel
              label={i18n.t('pricing.table.header.current_gross_margin')}
              tooltipText={i18n.t(
                'pricing.table.header.current_gross_margin.tooltip_text',
              )}
            />
          ),
          id: DetailColumnId.CurrentMargin,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.current_gross_margin'),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (a, b) =>
            (resolveEditedRow(a.original).grossMarginPercentage ?? 0) -
            (resolveEditedRow(b.original).grossMarginPercentage ?? 0),
          sortUndefined: 'last',
        },
      ),
      // GM% Δ
      columnHelper.accessor(
        (row) =>
          row.grossMarginPercentageDelta === null
            ? '--'
            : formatPercentage(row.grossMarginPercentageDelta, 1, 1),
        {
          cell: (info: DetailCellRenderInput) => {
            const row = info.row.original;
            const delta = row.grossMarginPercentageDelta;
            if (delta === null) {
              return <PricingTableDeltaCell value="--" />;
            }
            const { direction, formattedValue } = resolveDelta(delta, (v) =>
              formatPercentage(v, 1, 1),
            );
            if (info.isAggregateRow) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            }
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="grossMarginPercentageDelta"
                formatValue={(v) => formatPercentage(v, 1, 1)}
                originalArrowDirection={direction}
                originalFormattedValue={formattedValue}
                originalRow={adaptedRow(row)}
                originalValue={formattedValue}
                testID="original-gross-margin-percentage-delta"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.GrossMarginPercentageDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: DetailColumnId.MarginDelta,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.gross_margin_percentage_delta'),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (a, b) =>
            (resolveEditedRow(a.original).grossMarginPercentageDelta ?? 0) -
            (resolveEditedRow(b.original).grossMarginPercentageDelta ?? 0),
          sortUndefined: 'last',
        },
      ),
      // Last Price
      columnHelper.accessor(
        (row) =>
          row.lastPriceInMicros === null
            ? '--'
            : formatCurrency(row.lastPriceInMicros, currencyCode),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => i18n.t('pricing.table.header.last_price'),
          id: DetailColumnId.LastPrice,
          meta: {
            defaultHidden: true,
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.last_price'),
          },
          ...COLUMN_WIDTH.M,
          sortUndefined: 'last',
        },
      ),
      // Current Price — editable
      columnHelper.accessor(
        (row) =>
          row.priceInMicros === null
            ? '--'
            : formatCurrency(row.priceInMicros, currencyCode),
        {
          cell: (info: DetailCellRenderInput) => {
            const row = info.row.original;
            if (
              info.isAggregateRow ||
              row.priceInMicros === null ||
              row.costInMicros === null ||
              row.grossMarginPercentage === null
            ) {
              return (
                <PricingTableInputPlaceholder
                  color={theme.sk.color.text.primary}
                  value={info.getValue()}
                />
              );
            }
            return (
              <ItemInputCell
                canBookmarkItem={hasBookmarkingAccess}
                columnId={info.column.id}
                currencyCode={currencyCode}
                disabled={false}
                inputType={PricingEditField.Price}
                isHistoricalPricingContextEnabled={
                  isHistoricalPricingContextEnabled
                }
                originalFormattedValue={formatCurrency(
                  row.priceInMicros,
                  currencyCode,
                )}
                originalRow={adaptedRow(row)}
                pageSource={PricingPageSource.CUSTOMER_DETAIL}
                priceFloorEnabled={priceFloorEnabled}
                priceFloorMode={priceFloorMode}
                restaurantUUID={row.restaurantUUID}
                rowIndex={info.row.id}
                scope={editScope}
                tableRowIndex={info.index ?? info.row.index}
                value={String(row.priceInMicros / 1_000_000)}
                variantPackUUID={row.variantPackUUID}
              />
            );
          },
          header: () => i18n.t('pricing.table.header.current_price'),
          id: DetailColumnId.CurrentPrice,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.current_price'),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (a, b) =>
            (resolveEditedRow(a.original).priceInMicros ?? 0) -
            (resolveEditedRow(b.original).priceInMicros ?? 0),
          sortUndefined: 'last',
        },
      ),
      // Price Δ
      columnHelper.accessor(
        (row) =>
          row.priceDeltaInMicros === null
            ? '--'
            : formatCurrency(row.priceDeltaInMicros, currencyCode),
        {
          cell: (info: DetailCellRenderInput) => {
            const row = info.row.original;
            const delta = row.priceDeltaInMicros;
            if (delta === null) {
              return <PricingTableDeltaCell value="--" />;
            }
            const { direction, formattedValue } = resolveDelta(delta, (v) =>
              formatCurrency(v, currencyCode),
            );
            if (info.isAggregateRow) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            }
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="priceDeltaInMicros"
                formatValue={(v) => formatCurrency(v, currencyCode)}
                originalArrowDirection={direction}
                originalFormattedValue={formattedValue}
                originalRow={adaptedRow(row)}
                originalValue={formattedValue}
                testID="original-price-delta"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.PriceDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: DetailColumnId.PriceDelta,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.price_delta'),
          },
          ...COLUMN_WIDTH.S,
          sortingFn: (a, b) =>
            (resolveEditedRow(a.original).priceDeltaInMicros ?? 0) -
            (resolveEditedRow(b.original).priceDeltaInMicros ?? 0),
          sortUndefined: 'last',
        },
      ),
      // Last Cost
      columnHelper.accessor(
        (row) =>
          row.lastCostInMicros === null
            ? '--'
            : formatCurrency(row.lastCostInMicros, currencyCode),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => i18n.t('pricing.table.header.last_cost'),
          id: DetailColumnId.LastCost,
          meta: {
            defaultHidden: true,
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.last_cost'),
          },
          ...COLUMN_WIDTH.M,
          sortUndefined: 'last',
        },
      ),
      // Current Cost
      columnHelper.accessor(
        (row) =>
          row.costInMicros === null
            ? '--'
            : formatCurrency(row.costInMicros, currencyCode),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => i18n.t('pricing.table.header.current_cost'),
          id: DetailColumnId.CurrentCost,
          meta: {
            defaultHidden: true,
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.current_cost'),
          },
          ...COLUMN_WIDTH.M,
          sortUndefined: 'last',
        },
      ),
      // Cost Δ
      columnHelper.accessor(
        (row) =>
          row.costDeltaInMicros === null
            ? '--'
            : formatCurrency(row.costDeltaInMicros, currencyCode),
        {
          cell: ({ row }) => {
            const delta = row.original.costDeltaInMicros;
            if (delta === null) {
              return <PricingTableDeltaCell value="--" />;
            }
            const { direction, formattedValue } = resolveDelta(delta, (v) =>
              formatCurrency(v, currencyCode),
            );
            return (
              <PricingTableDeltaCell
                arrowDirection={direction}
                hasInvertedColor
                value={formattedValue}
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.CostDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: DetailColumnId.CostDelta,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.cost_delta'),
          },
          ...COLUMN_WIDTH.S,
          sortUndefined: 'last',
        },
      ),
      // GP/unit
      columnHelper.accessor(
        (row) =>
          row.grossProfitPerUnitInMicros === null
            ? '--'
            : formatCurrency(row.grossProfitPerUnitInMicros, currencyCode),
        {
          cell: (info: DetailCellRenderInput) => {
            const row = info.row.original;
            const value = info.getValue();
            if (
              info.isAggregateRow ||
              row.grossProfitPerUnitInMicros === null
            ) {
              return <Typography variant="paragraph-md">{value}</Typography>;
            }
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="grossProfitInMicros"
                formatValue={(v) => formatCurrency(v, currencyCode)}
                hideDeltaAttributes
                originalFormattedValue={value}
                originalRow={adaptedRow(row)}
                originalValue={value}
                testID="original-gross-profit-per-unit"
              />
            );
          },
          header: () => (
            <PricingTableHeader columnId={ColumnId.GrossProfit} withTooltip />
          ),
          id: DetailColumnId.GrossProfitPerUnit,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.gross_profit'),
          },
          ...COLUMN_WIDTH.M,
          sortUndefined: 'last',
        },
      ),
      // Orders L6W
      columnHelper.accessor(
        (row) =>
          row.weeklyPurchaseQuantities
            .reduce((acc, w) => acc + w.quantity, 0)
            .toString(),
        {
          cell: (info: DetailCellRenderInput) => {
            if (info.isAggregateRow) {
              return null;
            }
            return (
              <WeeklyPurchaseQuantitiesCell
                quantities={info.row.original.weeklyPurchaseQuantities}
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.WeeklyPurchaseQuantities}
              withTooltip
            />
          ),
          id: DetailColumnId.WeeklyPurchaseQuantities,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.weekly_purchase_quantities'),
          },
          ...COLUMN_WIDTH.S,
          sortingFn: (rowA, rowB) => {
            const sum = (xs: { quantity: number }[]) =>
              xs.reduce((acc, x) => acc + x.quantity, 0);
            return (
              sum(rowA.original.weeklyPurchaseQuantities) -
              sum(rowB.original.weeklyPurchaseQuantities)
            );
          },
        },
      ),
      // Avg Vol/wk
      columnHelper.accessor((row) => row.lineItems.toFixed(1), {
        cell: (info) => (
          <Typography variant="paragraph-md">{info.getValue()}</Typography>
        ),
        header: () => (
          <PricingTableHeader columnId={ColumnId.WeeklyVolume} withTooltip />
        ),
        id: DetailColumnId.Volume,
        meta: {
          initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
          label: i18n.t('pricing.table.header.weekly_volume'),
        },
        ...COLUMN_WIDTH.XS,
        sortingFn: (a, b) => a.original.lineItems - b.original.lineItems,
      }),
      // Vol/order
      columnHelper.accessor(
        (row) =>
          header && header.orderCount > 0
            ? (row.lineItems / header.orderCount).toFixed(1)
            : '--',
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.VolumePerOrder}
              withTooltip
            />
          ),
          id: DetailColumnId.VolumePerOrder,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.volume_per_order'),
          },
          ...COLUMN_WIDTH.S,
          sortingFn: (a, b) => a.original.lineItems - b.original.lineItems,
        },
      ),
      // Edits GP impact (edit-only; cost change stays the non-edited baseline)
      columnHelper.accessor(
        (row) =>
          formatCurrency(row.editsGrossProfitImpactInMicros, currencyCode),
        {
          cell: (info: DetailCellRenderInput) => {
            const { direction, formattedValue } = resolveDelta(
              info.row.original.editsGrossProfitImpactInMicros,
              (v) => formatCurrency(v, currencyCode),
            );
            if (info.isAggregateRow) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            }
            // Defer to the overlay even with no baseline so a pending edit's impact still shows.
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="editsGrossProfitImpactInMicros"
                formatValue={(v) => formatCurrency(v, currencyCode)}
                originalArrowDirection={direction}
                originalFormattedValue={formattedValue}
                originalRow={adaptedRow(info.row.original)}
                originalValue={formattedValue}
                testID="original-weekly-edits-impact"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.WeeklyEditsGrossProfitImpact}
              withTooltip
            />
          ),
          id: DetailColumnId.WeeklyEditsGrossProfitImpact,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.weekly_edits_impact'),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (a, b) =>
            resolveEditedRow(a.original).editsGrossProfitImpactInMicros -
            resolveEditedRow(b.original).editsGrossProfitImpactInMicros,
        },
      ),
      // Edits GP impact per order (weekly edits impact ÷ order count)
      columnHelper.accessor(
        (row) =>
          header && header.orderCount > 0
            ? formatCurrency(
                row.editsGrossProfitImpactInMicros / header.orderCount,
                currencyCode,
              )
            : '--',
        {
          cell: (info: DetailCellRenderInput) => {
            const formatPerOrder = (v: number) =>
              header && header.orderCount > 0
                ? formatCurrency(v / header.orderCount, currencyCode)
                : '--';
            const { direction, formattedValue } = resolveDelta(
              info.row.original.editsGrossProfitImpactInMicros,
              formatPerOrder,
            );
            if (info.isAggregateRow) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            }
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="editsGrossProfitImpactInMicros"
                formatValue={formatPerOrder}
                originalArrowDirection={direction}
                originalFormattedValue={formattedValue}
                originalRow={adaptedRow(info.row.original)}
                originalValue={formattedValue}
                testID="original-edits-impact-per-order"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.EditsGrossProfitImpactPerOrder}
              withTooltip
            />
          ),
          id: DetailColumnId.EditsGrossProfitImpactPerOrder,
          meta: {
            initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
            label: i18n.t('pricing.table.header.edits_impact_per_order'),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (a, b) =>
            resolveEditedRow(a.original).editsGrossProfitImpactInMicros -
            resolveEditedRow(b.original).editsGrossProfitImpactInMicros,
        },
      ),
      // Dynamic columns from price_edit_display_headers
      ...(priceEditDisplayHeaders ?? []).map((header) => {
        const label = getTranslationValue(header.translation);
        return columnHelper.accessor(
          (row) => row.supplierDisplayInfo?.[header.key]?.display ?? '',
          {
            cell: (info) => (
              <Typography variant="paragraph-md">{info.getValue()}</Typography>
            ),
            header: () => label,
            id: header.key,
            meta: {
              defaultHidden: true,
              initialPercentWidth: NUMERIC_COLUMN_PERCENTAGE,
              label,
            },
            ...COLUMN_WIDTH.M,
            sortingFn: (rowA, rowB) => {
              const a = rowA.original.supplierDisplayInfo?.[header.key]?.raw;
              const b = rowB.original.supplierDisplayInfo?.[header.key]?.raw;
              if (a === null || a === undefined) {
                return b === null || b === undefined ? 0 : -1;
              }
              if (b === null || b === undefined) {
                return 1;
              }
              if (typeof a === 'number' && typeof b === 'number') {
                return a - b;
              }
              return String(a).localeCompare(String(b));
            },
            sortUndefined: 'last',
          },
        );
      }),
    ];
  }, [
    adaptedRowsByVariantPack,
    costChangeLookbackMode,
    costChangeWeekAnchorDay,
    currencyCode,
    editScope,
    priceEditDisplayHeaders,
    header,
    hasBookmarkingAccess,
    isHistoricalPricingContextEnabled,
    navigation,
    NUMERIC_COLUMN_PERCENTAGE,
    priceFloorEnabled,
    priceFloorMode,
    resolveEditedRow,
    selectedVariantPackUUIDs,
    styles.checkbox,
    styles.checkboxContainer,
    theme.sk.color.text.primary,
    toggleVariantPackSelection,
  ]);

  const { columnOrder, columnVisibility } = useConfigureColumns(
    ConfigurationDomain.Pricing,
    TableName.PricingCustomerDetail,
    columns,
  );

  const variantPackEditCounts = useMemo(
    () =>
      edits.reduce<Record<string, number>>((acc, edit) => {
        if (edit.restaurantUUID === restaurantUUID) {
          acc[edit.variantPackUUID] = (acc[edit.variantPackUUID] ?? 0) + 1;
        }
        return acc;
      }, {}),
    [edits, restaurantUUID],
  );

  const bookmarks = usePricingBookmarksStore((state) => state.bookmarks);
  const bookmarkedByVariantPack = useMemo(() => {
    if (!hasBookmarkingAccess) {
      return undefined;
    }
    return rows.reduce<Record<string, boolean>>((acc, row) => {
      acc[row.variantPackUUID] =
        bookmarkKey(row.variantPackUUID, restaurantUUID) in bookmarks;
      return acc;
    }, {});
  }, [bookmarks, hasBookmarkingAccess, restaurantUUID, rows]);

  const rangeColumnConfigs = useMemo<
    Record<string, PricingRangeColumnConfig<PricingCustomerDetailRow>>
  >(() => {
    const currencyAffix = getCurrencySymbol(currencyCode);
    const orderCount = header?.orderCount ?? 0;

    return {
      [DetailColumnId.RealizedGrossMargin]: {
        accessor: (row) => row.lastMarginPercentage,
        affix: '%',
        title: i18n.t('pricing.table.header.realized_gross_margin'),
        unit: PricingFilterUnit.Percent,
      },
      [DetailColumnId.CurrentMargin]: {
        accessor: (row) => row.grossMarginPercentage,
        affix: '%',
        title: i18n.t('pricing.table.header.current_gross_margin'),
        unit: PricingFilterUnit.Percent,
      },
      [DetailColumnId.MarginDelta]: {
        accessor: (row) => row.grossMarginPercentageDelta,
        affix: '%',
        title: i18n.t('pricing.table.header.gross_margin_percentage_delta'),
        unit: PricingFilterUnit.Percent,
      },
      [DetailColumnId.LastPrice]: {
        accessor: (row) =>
          row.lastPriceInMicros === null
            ? null
            : row.lastPriceInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: i18n.t('pricing.table.header.last_price'),
        unit: PricingFilterUnit.Currency,
      },
      [DetailColumnId.CurrentPrice]: {
        accessor: (row) =>
          row.priceInMicros === null
            ? null
            : row.priceInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: i18n.t('pricing.table.header.current_price'),
        unit: PricingFilterUnit.Currency,
      },
      [DetailColumnId.PriceDelta]: {
        accessor: (row) =>
          row.priceDeltaInMicros === null
            ? null
            : row.priceDeltaInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        filterPillMetric: (row) =>
          percentOfLast(row.priceDeltaInMicros, row.lastPriceInMicros),
        title: i18n.t('pricing.table.header.price_delta'),
        unit: PricingFilterUnit.Currency,
      },
      [DetailColumnId.LastCost]: {
        accessor: (row) =>
          row.lastCostInMicros === null
            ? null
            : row.lastCostInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: i18n.t('pricing.table.header.last_cost'),
        unit: PricingFilterUnit.Currency,
      },
      [DetailColumnId.CurrentCost]: {
        accessor: (row) =>
          row.costInMicros === null
            ? null
            : row.costInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: i18n.t('pricing.table.header.current_cost'),
        unit: PricingFilterUnit.Currency,
      },
      [DetailColumnId.CostDelta]: {
        accessor: (row) =>
          row.costDeltaInMicros === null
            ? null
            : row.costDeltaInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        filterPillMetric: (row) =>
          percentOfLast(row.costDeltaInMicros, row.lastCostInMicros),
        title: i18n.t('pricing.table.header.cost_delta'),
        unit: PricingFilterUnit.Currency,
      },
      [DetailColumnId.GrossProfitPerUnit]: {
        accessor: (row) =>
          row.grossProfitPerUnitInMicros === null
            ? null
            : row.grossProfitPerUnitInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: i18n.t('pricing.table.header.gross_profit'),
        unit: PricingFilterUnit.Currency,
      },
      [DetailColumnId.Volume]: {
        accessor: (row) => row.lineItems,
        affix: 'u',
        title: i18n.t('pricing.table.header.weekly_volume'),
        unit: PricingFilterUnit.Unit,
      },
      [DetailColumnId.VolumePerOrder]: {
        accessor: (row) => (orderCount > 0 ? row.lineItems / orderCount : null),
        affix: 'u',
        title: i18n.t('pricing.table.header.volume_per_order'),
        unit: PricingFilterUnit.Unit,
      },
      [DetailColumnId.WeeklyEditsGrossProfitImpact]: {
        accessor: (row) =>
          row.editsGrossProfitImpactInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: i18n.t('pricing.table.header.weekly_edits_impact'),
        unit: PricingFilterUnit.Currency,
      },
      [DetailColumnId.EditsGrossProfitImpactPerOrder]: {
        accessor: (row) =>
          orderCount > 0
            ? row.editsGrossProfitImpactInMicros / orderCount / MICRO_CONVERSION
            : null,
        affix: currencyAffix,
        title: i18n.t('pricing.table.header.edits_impact_per_order'),
        unit: PricingFilterUnit.Currency,
      },
    };
  }, [currencyCode, header?.orderCount]);

  const filterColumnIds = useMemo(
    () =>
      columns
        .map((column) => column.id)
        .filter((id): id is string => Boolean(id)),
    [columns],
  );

  // These columns have no i18n key of their own, so the filter section reuses
  // the same translated header label the column does.
  const displayInfoTitles = useMemo(
    () =>
      (priceEditDisplayHeaders ?? []).reduce<Record<string, string>>(
        (acc, header) => {
          acc[header.key] = getTranslationValue(header.translation);
          return acc;
        },
        {},
      ),
    [priceEditDisplayHeaders],
  );

  const filterSections = useMemo(
    () =>
      buildCustomerDetailFilterSections({
        bookmarkedByVariantPack,
        columnIds: filterColumnIds,
        displayInfoTitles,
        editCounts: variantPackEditCounts,
        rangeColumnConfigs,
        rows: mergedRows,
        timezone,
      }),
    [
      bookmarkedByVariantPack,
      displayInfoTitles,
      filterColumnIds,
      mergedRows,
      rangeColumnConfigs,
      timezone,
      variantPackEditCounts,
    ],
  );

  const filters = usePricingFilters(
    PricingFilterScope.CustomerDetail,
    filterSections,
  );
  const panel = usePricingFilterPanel();

  const handleFiltersUrlUpdate = useCallback(
    (params: Record<string, string | undefined>) =>
      navigation.setParams(params),
    [navigation],
  );

  usePricingFiltersUrlSync({
    hydrate: filters.hydrate,
    onUrlUpdate: handleFiltersUrlUpdate,
    routeParams: route.params,
    sections: filterSections,
    selections: { pills: filters.pills, ranges: filters.ranges },
  });

  // Drop filters whose column is gone, so they can't silently keep shrinking
  // the table. Depends on the store action, not `filters`, which would loop.
  useEffect(() => {
    filters.pruneSections(filterSections.map((section) => section.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSections, filters.pruneSections]);

  // Store slices, not `filters`: that object is new every render, so the
  // Table would get a new array on every keystroke.
  const { pills: filterPills, ranges: filterRanges } = filters;
  // Filters judge the post-edit rows (mergedRows), but the table itself keeps
  // rendering the unedited baseline rows: cells like ItemInputCell diff
  // against that baseline to compute their own edit overlay.
  const filteredRows = useMemo(() => {
    const passingVariantPackUUIDs = new Set(
      applyPricingFilters(
        mergedRows,
        { pills: filterPills, ranges: filterRanges },
        filterSections,
      ).map((row) => row.variantPackUUID),
    );
    return rows.filter((row) =>
      passingVariantPackUUIDs.has(row.variantPackUUID),
    );
  }, [filterPills, filterRanges, mergedRows, rows, filterSections]);

  const handleClearEmptyState = useCallback(() => {
    if (debouncedSearchText.length > 0) {
      clearSearch();
    } else {
      filters.clearAll();
    }
  }, [clearSearch, debouncedSearchText, filters]);

  const renderHeaderRow = useCallback<
    RenderHeaderRowCallback<PricingCustomerDetailRow>
  >(
    ({ headerGroup }) => (
      <React.Fragment key={headerGroup.id}>
        <PricingTableHeaderCells
          headerGroup={headerGroup}
          isLeftAlignedColumn={isLeftAlignedColumn}
          sorting={sorting}
        />
        {aggregateRow ? (
          <PricingAggregateRow
            data={aggregateRow}
            editsCount={customerEditsCount}
            headers={headerGroup.headers}
            isDeltaColumn={isDeltaColumn}
            isLeftAlignedColumn={isLeftAlignedColumn}
            testID="pricing-customer-detail-aggregate-row"
          />
        ) : null}
      </React.Fragment>
    ),
    [aggregateRow, customerEditsCount, sorting],
  );

  const renderRow = useCallback<RenderRowCallback<PricingCustomerDetailRow>>(
    ({ index, row }) => {
      const hasChanges = edits.some(
        (edit) =>
          edit.variantPackUUID === row.original.variantPackUUID &&
          edit.restaurantUUID === row.original.restaurantUUID,
      );
      return (
        <PricingCustomerDetailTableRow
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          hasChanges={hasChanges}
          index={index}
          key={row.id}
          row={row}
        />
      );
    },
    [columnOrder, columnVisibility, edits],
  );

  // A fresh array here would churn the table context and every cell in it.
  const cellPadding = useMemo(
    () => [
      // Checkbox column keeps the wider padding so its
      // right-aligned checkbox isn't clipped.
      theme.sk.spacing.spaceMedium,
      ...Array(columns.length - 1).fill(theme.sk.spacing.spaceSmall),
    ],
    [columns.length, theme.sk.spacing.spaceMedium, theme.sk.spacing.spaceSmall],
  );

  const LoadingSkeleton = () => (
    <View style={styles.loadingContent}>
      <SearchSkeleton />
      <PricingTableSkeleton />
    </View>
  );

  return (
    <SalesHubShellTemplate>
      <AppPageLayout
        actions={
          <View
            style={[
              styles.topBarActions,
              panel.isOpen && styles.actionsWithPanelOpen,
            ]}
          >
            {hasUndoHistory ? <PricingUndoButton onPress={handleUndo} /> : null}
            {hasPriceEdits ? (
              <PricingReviewButton
                onPress={() =>
                  navigation.navigate(
                    SALES_PORTAL_ROUTES.PricingManagementReview,
                  )
                }
              />
            ) : null}
          </View>
        }
        leadingActions={
          <PricingBreadcrumbs
            parentRoute={SALES_PORTAL_ROUTES.PricingCustomers}
            secondaryBreadcrumb={i18n.t('pricing.customers.sidenav_title')}
            tertiaryBreadcrumb={header?.restaurantAccountId}
          />
        }
        overlay={
          panel.isOpen ? (
            <PricingFilterPanel
              filters={filters}
              onClose={panel.close}
              sections={filterSections}
            />
          ) : null
        }
        scroll={false}
      >
        <View
          style={[
            styles.container,
            panel.isOpen && styles.containerWithPanelOpen,
          ]}
        >
          {loading || timezoneLoading ? (
            <LoadingSkeleton />
          ) : error ? (
            <EmptyState
              bottomElement={
                <Button
                  accessibilityLabel={i18n.t('sales_portal.error.retry')}
                  onPress={async () => await refetch()}
                  style={styles.autoAlignSelf}
                  variant="secondary"
                >
                  {i18n.t('sales_portal.error.retry')}
                </Button>
              }
              description={i18n.t('pricing.error.table.description')}
              iconElement={
                <CircleWarningIcon stroke={theme.sk.color.icon.primary} />
              }
              title={i18n.t('pricing.error.table.title')}
            />
          ) : (
            <Suspense fallback={<LoadingSkeleton />}>
              <PricingSearchBar
                trailing={
                  <>
                    <PricingFilterButton
                      activeCount={filters.activeCount}
                      onPress={panel.open}
                    />
                    <ConfigureColumnsButton
                      columns={columns}
                      domain={ConfigurationDomain.Pricing}
                      table={TableName.PricingCustomerDetail}
                    />
                  </>
                }
              >
                <SearchInput
                  onSearchChange={handleSearchChange}
                  placeholderText={i18n.t('pricing.search_placeholder')}
                  ref={searchInputRef}
                  searchText={searchText}
                />
              </PricingSearchBar>
              <View onLayout={handleTableLayout} style={styles.tableWrapper}>
                <Table
                  cellPadding={cellPadding}
                  columns={columns}
                  data={filteredRows}
                  emptyComponent={
                    <PricingFilterEmptyState
                      baseEmptyComponent={
                        <PricingCustomerDetailTableEmptyComponent />
                      }
                      hasActiveFilters={filters.hasActiveFilters}
                      onClear={handleClearEmptyState}
                      searchText={debouncedSearchText}
                    />
                  }
                  estimatedRowHeight={PRICING_TABLE_LAYOUT.ESTIMATED_ROW_HEIGHT}
                  fillAvailableWidth
                  globalFilterFn={customerDetailGlobalFilterFn}
                  headerPadding={theme.sk.spacing.spaceSmall}
                  initialState={{
                    columnPinning: {
                      left: [DetailColumnId.Checkbox, DetailColumnId.Name],
                    },
                  }}
                  isActive
                  layoutWidth={tableLayoutWidth ?? undefined}
                  onFilteredRowsChange={onFilteredRowsChange}
                  onSortingChange={onSortingChange}
                  renderHeaderRow={renderHeaderRow}
                  renderRow={renderRow}
                  sectionHeader={
                    filteredRows.length > 0 ? (
                      <PricingSelectAllRow
                        disabled={isAllLocked}
                        isAllSelected={isAllSelected}
                        isPartiallySelected={isPartiallySelected}
                        label={i18n.t(
                          filteredCount === 1
                            ? 'pricing.table.select_all_items.singular'
                            : 'pricing.table.select_all_items.plural',
                          { count: filteredCount },
                        )}
                        onToggleAll={toggleAllVisible}
                      />
                    ) : null
                  }
                  state={{
                    columnOrder,
                    columnVisibility,
                    globalFilter: debouncedSearchText,
                    sorting,
                  }}
                />
                {isBulkEditBarVisible ? (
                  <PricingBulkEditBottomBar
                    canApply={canApplyBulkInput}
                    editField={bulkEditField}
                    editMode={bulkEditMode}
                    isBookmarkNoteOpen={isBookmarkNoteOpen}
                    onApply={handleApplyBulkInput}
                    onEditFieldChange={setBulkEditField}
                    onEditModeChange={setBulkEditMode}
                    onInputValueChange={setBulkInputValue}
                    onSaveBookmarkNote={onSaveBookmarkNote}
                    quickActions={bulkQuickActions}
                    selectedCount={selectedCount}
                    selectedCountLabel={i18n.t(
                      selectedCount === 1
                        ? 'pricing.bulk_edit.item_count.singular'
                        : 'pricing.bulk_edit.item_count.plural',
                      { count: selectedCount },
                    )}
                    value={bulkInputValue}
                  />
                ) : null}
              </View>
            </Suspense>
          )}
        </View>
      </AppPageLayout>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  actionsWithPanelOpen: {
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  autoAlignSelf: {
    alignSelf: 'auto',
  },
  checkbox: {
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
    paddingVertical: 14,
  },
  checkboxContainer: {
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    // Use negative margin to increase interaction target
    marginHorizontal:
      -theme.sk.spacing.spaceLarge - theme.sk.spacing.spaceXsmall,
    marginVertical: -theme.sk.spacing.spaceSMedium,
  },
  container: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  containerWithPanelOpen: {
    minWidth: PRICING_CONTENT_MIN_WIDTH + PRICING_FILTER_PANEL_WIDTH,
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  loadingContent: {
    flex: 1,
    gap: 42,
  },
  tableWrapper: {
    flex: 1,
    position: 'relative',
  },
  topBarActions: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
